import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CrisisResourceBanner from '../../components/CrisisResourceBanner';
import SessionStatusIndicator from '../../components/SessionStatusIndicator';
import VideoInterface from './components/VideoInterface';
import SessionControls from './components/SessionControls';
import SessionInfo from './components/SessionInfo';
import EmergencyPanel from './components/EmergencyPanel';
import ChatPanel from './components/ChatPanel';
import SettingsPanel from './components/SettingsPanel';
import TechnicalSupport from './components/TechnicalSupport';
import EngagementMetrics from './components/EngagementMetrics';
import EndSessionConfirmation from './components/EndSessionConfirmation';
import Button from '../../components/ui/Button';
import { sessionService } from '../../services/sessionService';
import { useSessionInitialization } from './hooks/useSessionInitialization';
import { useSessionTimer, useConnectionQuality } from './hooks/useSessionTimer';
import { useChatMessages } from './hooks/useChatMessages';
import { useEngagementMetrics } from './hooks/useEngagementMetrics';

const SessionRoom = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionStartTime] = useState(new Date());

  // Session initialization
  const { currentSessionId, appointmentData, loading, error } = useSessionInitialization(location);

  // Session state
  const [sessionActive, setSessionActive] = useState(true);
  const sessionDuration = useSessionTimer(sessionActive);
  const connectionQuality = useConnectionQuality();

  // Media controls
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  // Panel states
  const [activePanel, setActivePanel] = useState('chat');
  const [showEmergencyPanel, setShowEmergencyPanel] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [showTechnicalSupport, setShowTechnicalSupport] = useState(false);
  const [showEndConfirmation, setShowEndConfirmation] = useState(false);

  // Settings
  const [settings, setSettings] = useState({
    audioInput: 'default',
    audioOutput: 'default',
    videoInput: 'default',
    videoQuality: 'auto',
    echoCancellation: true,
    noiseSuppression: true,
    mirrorVideo: true,
    showConnectionQuality: true,
    chatBackup: true,
    captions: false,
    highContrast: false
  });

  // Chat messages
  const { chatMessages, handleSendMessage } = useChatMessages(currentSessionId);

  // Engagement metrics
  const engagementMetrics = useEngagementMetrics(
    currentSessionId,
    sessionActive,
    sessionDuration,
    connectionQuality,
    chatMessages,
    isVideoEnabled,
    isAudioEnabled,
    appointmentData
  );

  // Session data
  const sessionData = {
    sessionId: 'SES-2025-001234',
    participantName: 'Dr. Sarah Johnson',
    participantRole: 'Licensed Clinical Psychologist',
    participantAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_116492473-1763296099355.png",
    participantAvatarAlt: 'Professional headshot of Dr. Sarah Johnson, a woman with shoulder-length brown hair wearing a navy blazer and warm smile',
    startTime: '2025-12-04T06:05:00',
    isRecording: isRecording,
    helperMode: false,
    canRecord: true,
    canShareScreen: true
  };

  const handleEndSession = async () => {
    if (!confirm('Are you sure you want to end this session?')) return;

    if (currentSessionId) {
      const duration = Math.floor((new Date() - sessionStartTime) / 60000);
      await sessionService?.endSession(currentSessionId, duration);
    }

    navigate('/personal-dashboard');
  };

  const handleEmergencyEscalate = (type) => {
    console.log('Emergency escalation:', type);
    if (type === 'immediate') {
      navigate('/organization-landing', {
        state: {
          emergencyEscalation: true,
          sessionId: sessionData?.sessionId
        }
      });
    }
  };

  const confirmEndSession = () => {
    setSessionActive(false);
    navigate('/personal-dashboard', {
      state: {
        sessionCompleted: true,
        sessionId: sessionData?.sessionId,
        duration: sessionDuration
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-background flex flex-col overflow-hidden pt-14">
      <CrisisResourceBanner />
      <SessionInfo sessionData={sessionData} duration={sessionDuration} />
      
      <div className="flex-1 relative overflow-hidden">
        <VideoInterface
          localStream={null}
          remoteStream={null}
          isVideoEnabled={isVideoEnabled}
          isAudioEnabled={isAudioEnabled}
          connectionQuality={connectionQuality}
          onToggleVideo={() => setIsVideoEnabled(!isVideoEnabled)}
          onToggleAudio={() => setIsAudioEnabled(!isAudioEnabled)} />


        <div className="absolute top-4 left-4 w-72">
          <EngagementMetrics metrics={engagementMetrics} />
        </div>

        <Button
          variant="destructive"
          size="lg"
          iconName="AlertTriangle"
          iconPosition="left"
          onClick={() => setShowEmergencyPanel(!showEmergencyPanel)}
          className="absolute top-4 right-4">

          Emergency Resources
        </Button>

        <Button
          variant="outline"
          size="icon"
          iconName="HelpCircle"
          onClick={() => setShowTechnicalSupport(true)}
          className="absolute bottom-4 left-4 bg-card"
          title="Technical support" />


        {showEmergencyPanel &&
        <EmergencyPanel
          onEscalate={handleEmergencyEscalate}
          helperMode={sessionData?.helperMode} />

        }

        {activePanel === 'chat' &&
        <ChatPanel
          isOpen={activePanel === 'chat'}
          onClose={() => setActivePanel(null)}
          participantName={sessionData?.participantName}
          messages={chatMessages}
          onSendMessage={handleSendMessage} />

        }
      </div>

      <SessionControls
        isVideoEnabled={isVideoEnabled}
        isAudioEnabled={isAudioEnabled}
        isScreenSharing={isScreenSharing}
        onToggleVideo={() => setIsVideoEnabled(!isVideoEnabled)}
        onToggleAudio={() => setIsAudioEnabled(!isAudioEnabled)}
        onToggleScreenShare={() => setIsScreenSharing(!isScreenSharing)}
        onOpenChat={() => setActivePanel('chat')}
        onOpenSettings={() => setShowSettingsPanel(true)}
        isRecording={isRecording}
        onToggleRecording={() => setIsRecording(!isRecording)}
        onEndSession={handleEndSession}
        canRecord={sessionData?.canRecord}
        canShareScreen={sessionData?.canShareScreen} />


      <SettingsPanel
        isOpen={showSettingsPanel}
        onClose={() => setShowSettingsPanel(false)}
        settings={settings}
        onUpdateSettings={setSettings} />


      <TechnicalSupport
        isOpen={showTechnicalSupport}
        onClose={() => setShowTechnicalSupport(false)} />


      <SessionStatusIndicator
        sessionActive={sessionActive}
        sessionData={{
          startTime: sessionData?.startTime,
          helperName: sessionData?.participantName
        }} />


      <EndSessionConfirmation
        show={showEndConfirmation}
        sessionData={sessionData}
        sessionDuration={sessionDuration}
        onCancel={() => setShowEndConfirmation(false)}
        onConfirm={confirmEndSession} />


      {loading &&
      <div className="h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            <p className="mt-4 text-white">Initializing session...</p>
          </div>
        </div>
      }

      {error &&
      <div className="h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <p className="text-error mb-4">{error}</p>
            <button
            onClick={() => navigate('/personal-dashboard')}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">

              Return to Dashboard
            </button>
          </div>
        </div>
      }
    </div>);

};

export default SessionRoom;